import React from 'react';

import { AccountBalance } from '@eika-kortbetjening/types';
import { RowButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';

import { Format } from '@/domain/app';
import useText, { KortbetjeningText } from '@/hooks/use-text';
import { formatAmount } from '@/utils/text-format-utils';

import { PayCardData } from './types';

interface AccountButtonProps {
    card: PayCardData;
    accountBalance: AccountBalance;
    onClick: () => void;
    error?: string;
    label?: string;
    className?: string;
    disabled: boolean;
    showIcon?: boolean;
    placeholder: string;
    locked?: boolean;
}

const getIcon = (isLocked: boolean, text: KortbetjeningText) => {
    if (isLocked) {
        return <Icon type={IconTypes.PIN_KODE_24} ariaLabel={text.locked} />;
    }

    return IconTypes.LITEN_PIL_HOEYRE;
};

export const AccountButton: React.FC<AccountButtonProps> = ({
    card,
    accountBalance,
    label,
    className,
    onClick,
    error,
    disabled,
    showIcon = false,
    placeholder,
    locked = false,
}) => {
    const text = useText();

    const { saldo, limit } = accountBalance;

    const ariaLabel = () => {
        if (!card) {
            return `Velg ${label} kort`;
        }

        const { id } = card;

        return `${label ?? ''} ${card.type}, ${saldo}, på kort ${id}`;
    };

    const prettyCardName = (name?: string) => name?.replace('_', ' ');

    return (
        <RowButton
            className={className}
            onClick={onClick}
            disabled={disabled || locked}
            data-testid={`${label}-account-select`}
            aria-label={ariaLabel()}
        >
            <RowButton.AccountSelectorLayout
                placeholder={placeholder}
                label={label}
                accountAlias={prettyCardName(card.productName) ?? ''}
                accountNumber={card && Format.cardNumber(card.number)}
                amount={formatAmount(saldo)}
                error={error}
                credit={card && formatAmount(-Math.abs(limit), { maximumFractionDigits: 0 })}
                icon={showIcon ? getIcon(locked, text) : undefined}
            />
        </RowButton>
    );
};
